import * as Sentry from '@sentry/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { CART_KEY, useCart } from 'hooks/cart/useCart';
import { useCartAttributes } from 'hooks/cart/useCartAttributes';
import { useAnalytics } from 'hooks/useAnalytics';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import { useSession } from 'next-auth/react';
import { useState } from 'react';
import { ShopifyService } from 'services/shopify';
import { mapShopifyCart } from 'utils/mappers/mapShopifyCart';

export const useCheckout = () => {
  const { analytics } = useAnalytics();
  const queryClient = useQueryClient();
  const { data: cart } = useCart();
  const {
    value: { countryCode },
  } = useLocalStorage(UserLocale);
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';
  const [isCheckoutLoading, setIsCheckoutLoading] = useState<boolean>(false);
  const { getCartAttributes } = useCartAttributes();

  const createCheckoutURL = async () => {
    if (!cart?.checkoutUrl) return;
    setIsCheckoutLoading(true);
    analytics.beginCheckout({ cart });

    const attributes = await getCartAttributes();

    if (cart.id && attributes.length) {
      /* cartAttributesUpdate replaces the cart's whole attribute set, so carry
       * over any existing attributes that aren't being overwritten. */
      const keys = new Set(attributes.map(({ key }) => key));
      const existing = cart.attributes.filter(({ key }) => !keys.has(key));

      try {
        const { cartAttributesUpdate } =
          await ShopifyService.updateCartAttributes({
            cartId: cart.id,
            attributes: [...existing, ...attributes],
          });

        if (cartAttributesUpdate?.userErrors?.length) {
          Sentry.captureMessage('Failed to set cart attributes', {
            level: 'warning',
            extra: { userErrors: cartAttributesUpdate.userErrors },
          });
        }

        /* Keep the cached cart's attributes current, so a repeat checkout
         * (e.g. back from Shopify via bfcache) reuses the same polar_attr. */
        if (cartAttributesUpdate?.cart) {
          queryClient.setQueriesData(
            { queryKey: [CART_KEY, countryCode] },
            mapShopifyCart(cartAttributesUpdate.cart),
          );
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    const checkoutUrl = new URL(cart.checkoutUrl);
    checkoutUrl.searchParams.append('logged_in', `${isAuthenticated}`);

    window.location.assign(checkoutUrl.href);
  };

  return { isCheckoutLoading, setIsCheckoutLoading, createCheckoutURL };
};
